import { useAppDispatch, useAppSelector } from "@app/hooks"
import { ProgramManualUnlockClient } from "@clients/ProramManualUnlockClient"
import type {
  UnlockableProgram,
  UnlockableWeek,
} from "@clients/ProramManualUnlockClient"
import GenericDialog from "@cmp/genericDialog"
import { unlockNextWeekPopupUpdated } from "@features/program/programSlice"
import type { UnlockNextWeekPopup as UnlockNextWeekPopupModel } from "@features/program/programSlice"
import { showSnackbarItem, SnackbarDuration } from "@features/ui/uiSlice"
import type { ProgramDto } from "@masterschool/course-builder-api"
import {
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { formatDateAsMonthAndDay } from "@utils/date"
import { useState } from "react"
import {
  formatProgramName,
  formatProgramStartDate,
} from "../../../tab-views/students-tab/programLabel"

function UnlockNextWeekPopup() {
  const model = useAppSelector(
    (state) => state.program.studentsPage.unlockNextWeekPopup,
  )
  const dispatch = useAppDispatch()

  const onClose = () => dispatch(unlockNextWeekPopupUpdated(undefined))

  if (!model) {
    return null
  }
  return <UnlockWeeksDialog model={model} onClose={onClose} />
}

function UnlockWeeksDialog({
  model,
  onClose,
}: {
  model: UnlockNextWeekPopupModel
  onClose: () => void
}) {
  const dispatch = useAppDispatch()
  const storePrograms = useAppSelector((state) => state.program.programs)

  const programs = model.programs.filter((program) => program.courses.length)
  const initialProgram =
    programs.find((program) => program.programId === model.viewedProgramId) ??
    programs[0]

  const [selectedOfferingClientId, setSelectedOfferingClientId] = useState(
    initialProgram?.offeringClientId,
  )
  const [selectedSprintIds, setSelectedSprintIds] = useState<string[]>(() =>
    nextWeekPerCourse(initialProgram),
  )

  const selectedProgram = programs.find(
    (program) => program.offeringClientId === selectedOfferingClientId,
  )

  const onProgramChange = (offeringClientId: string) => {
    setSelectedOfferingClientId(offeringClientId)
    setSelectedSprintIds(
      nextWeekPerCourse(
        programs.find(
          (program) => program.offeringClientId === offeringClientId,
        ),
      ),
    )
  }

  const toggleSprint = (sprintId: string) =>
    setSelectedSprintIds((selected) =>
      selected.includes(sprintId)
        ? selected.filter((id) => id !== sprintId)
        : [...selected, sprintId],
    )

  const onConfirm = () => {
    ProgramManualUnlockClient.unlockSprints(model.studentId, selectedSprintIds)
      .then(() => {
        dispatch(
          showSnackbarItem({
            message: `Unlocked ${selectedSprintIds.length} content week(s)`,
            type: "success",
            duration: SnackbarDuration.short,
          }),
        )
        onClose()
      })
      .catch(() =>
        dispatch(
          showSnackbarItem({
            message: "Failed to unlock content weeks",
            type: "error",
            duration: SnackbarDuration.short,
          }),
        ),
      )
  }

  const hasUnlockableWeeks = programs.length > 0

  return (
    <GenericDialog
      open
      onClose={onClose}
      size="sm"
      title={`Unlock content weeks — ${model.firstName} ${model.lastName}`}
      content={
        hasUnlockableWeeks ? (
          <Stack gap={2} overflow="hidden">
            <Typography variant="body2" color="text.secondary">
              Unlocking does not change the student's calendar or add live
              sessions.
            </Typography>
            {programs.length > 1 && (
              <TextField
                select
                label="Program"
                value={selectedOfferingClientId}
                onChange={(event) => onProgramChange(event.target.value)}
              >
                {programs.map((program) => (
                  <MenuItem
                    key={program.offeringClientId}
                    value={program.offeringClientId}
                  >
                    {programLabel(program, storePrograms)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Typography variant="body1_sb">
              Select the content weeks you want to unlock for this student:
            </Typography>
            <Stack gap={2} maxHeight="70vh" overflow="auto">
              {selectedProgram?.courses.map((course) => (
                <Stack key={course.courseTitle}>
                  <Typography variant="subtitle2">
                    {course.courseTitle}
                  </Typography>
                  <List dense disablePadding>
                    {course.weeks.map((week) => (
                      <ListItemButton
                        key={week.sprintId}
                        dense
                        disableGutters
                        onClick={() => toggleSprint(week.sprintId)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedSprintIds.includes(week.sprintId)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Week ${week.weekNumber}: ${week.sprintTitle}`}
                          secondary={formatWeekDates(week)}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Typography variant="body2">
            There is nothing to unlock — all upcoming content weeks are already
            unlocked.
          </Typography>
        )
      }
      buttons={[
        {
          type: "secondary",
          disabled: true,
          text: `Selected: ${selectedSprintIds.length}`,
          onClick: () => {},
          align: "start",
        },
        { type: "secondary", text: "Cancel", onClick: onClose },
        {
          type: "primary",
          text: "Unlock",
          onClick: onConfirm,
          disabled: selectedSprintIds.length === 0,
        },
      ]}
    />
  )
}

function nextWeekPerCourse(program: UnlockableProgram | undefined): string[] {
  if (!program) {
    return []
  }
  return program.courses
    .map((course) => course.weeks[0]?.sprintId)
    .filter((sprintId): sprintId is string => !!sprintId)
}

function programLabel(
  program: UnlockableProgram,
  storePrograms: ProgramDto[],
): string {
  const storeProgram = storePrograms.find(
    (candidate) => candidate.id === program.programId,
  )
  if (storeProgram) {
    return formatProgramName(storeProgram)
  }
  return [
    program.syllabusName,
    formatProgramStartDate(program.offeringStartDate),
  ]
    .filter(Boolean)
    .join(" · ")
}

function formatWeekDates(week: UnlockableWeek): string {
  return `${formatDateAsMonthAndDay(
    week.startDate,
  )} – ${formatDateAsMonthAndDay(week.endDate)}`
}

export default UnlockNextWeekPopup
